"use client";

import { useMemo } from "react";
import {
  assertLaunchPayloadSize,
  stripThumbnailsFromLaunchPayload,
} from "./launchPayloadFilter";
import { useMetaPlatform } from "./provider";

// --- Asset Operations ---

export function usePreviewAssets() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.previewAssets.useMutation();
}

export function useValidateAssets() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.validateAssets.useMutation();
}

// --- Batch Creation & Launch ---

export function useCreateBatch() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.createBatch.useMutation();
}

export function usePreflightMeta() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.preflightMeta.useMutation();
}

export function useValidateMetaConfig() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.validateMetaConfig.useMutation();
}

export function useLogUnmappedError() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.logUnmappedValidationError.useMutation();
}

/**
 * v2 launch — hits `creativeTestingRouter.v2.launchWithAssets` instead of the
 * retired v1 path. Carries the TikTok schedule injection
 * (scheduleType=SCHEDULE_START_END + formatted start/end times).
 */
export function useLaunchBatchV2() {
  const { trpc } = useMetaPlatform();
  const base = trpc.creativeTestingRouter.v2.launchWithAssets.useMutation();

  return useMemo(() => {
    type MutatePayload = Parameters<typeof base.mutate>[0];

    const prepare = (payload: MutatePayload): MutatePayload => {
      const stripped = stripThumbnailsFromLaunchPayload(payload);
      assertLaunchPayloadSize(stripped);
      return stripped;
    };

    return {
      ...base,
      mutate: (
        payload: MutatePayload,
        options?: Parameters<typeof base.mutate>[1],
      ) => base.mutate(prepare(payload), options),
      mutateAsync: (
        payload: MutatePayload,
        options?: Parameters<typeof base.mutateAsync>[1],
      ) => base.mutateAsync(prepare(payload), options),
    };
  }, [base]);
}

// --- Batch Management ---

export function useDeleteBatch() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.deleteBatch.useMutation();
}

// --- Planned Batches ---

export function useCreatePlannedBatch() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.createPlannedBatch.useMutation();
}

export function useUpdatePlannedBatch() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.updatePlannedBatch.useMutation();
}

export function useSaveDraftAssets() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.saveDraftAssets.useMutation();
}

// --- AI ---

export function useGenerateBatchObjective() {
  const { trpc } = useMetaPlatform();
  return trpc.creativeTestingRouter.generateBatchObjective.useMutation();
}

// --- Copy Presets ---

export function useCreateCopyPreset() {
  const { trpc } = useMetaPlatform();
  return trpc.organization.createCopyPreset.useMutation();
}

export function useUpdateCopyPreset() {
  const { trpc } = useMetaPlatform();
  return trpc.organization.updateCopyPreset.useMutation();
}

export function useDeleteCopyPreset() {
  const { trpc } = useMetaPlatform();
  return trpc.organization.deleteCopyPreset.useMutation();
}
